import type { Option } from '@byecode/ui'
import { Box, Divider, SegmentedControl, Text } from '@byecode/ui'
import type { FlowNode, NodeTypes, SendEmailActionConfig } from '@lighthouse/shared'
import {
    DEVICE_ID_OPTION,
    EMAIL_EDIT_TYPE,
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    NodeFieldPreview,
    nodeTypeIconMap,
    OUTPUT_MULTIPLE_RECORD_NODES,
    PAGE_LINK_OPTION,
    useAtomData
} from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { useSetState } from 'react-use'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import type { WeComAccountMode } from '@/components/OtherPlatFormModal'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { ActionAuthIntegrateCard } from '../../Common/ActionAuthIntegrateCard'
import { FormItem } from '../../Common/FormItem'
import { GroupRequired } from '../../Common/GroupRequired'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as CM from '../../styles'
import { SendEmailProvider } from './context'
import { EmailParagraph } from './EmailParagraph'
import { EmailStyle } from './EmailStyle'
import { SelectEmail } from './NodeFieldVariable'

interface SendEmailActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: SendEmailActionConfig
    onChange: (value: SendEmailActionConfig) => void
}

interface State {
    mode: WeComAccountMode
    opened: boolean
    id?: string
}

const emailEditType = [
    {
        label: '富文本',
        value: EMAIL_EDIT_TYPE.RICHTEXT
    },
    {
        label: 'HTML',
        value: EMAIL_EDIT_TYPE.HTML
    }
]

export const SendEmailActionConfigure: React.FC<SendEmailActionConfigureProps> = ({ allParentNodes, value, onChange }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const [{ opened, mode, id }, setState] = useSetState<State>({
        opened: false,
        mode: 'add'
    })

    const { type } = useFlowContainerContext()
    const { senderAccountType, body, enableCc, title, recipients, cc } = value
    const editType = body?.editType
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    // const accountList: AllOtherPlatform = useMemo(
    //     () =>
    //         ({
    //             NET_EASE_126_MAIL: otherPlatformList?.filter(item => item.type === 'NET_EASE_126_MAIL'),
    //             WE_COM_MAIL: otherPlatformList?.filter(item => item.type === 'WE_COM_MAIL')
    //         }[senderAccountType] ?? []),
    //     [otherPlatformList, senderAccountType]
    // )

    const multiNodeOptions = useMemo(
        () =>
            allParentNodes.reduce<Option[]>((nodes, item) => {
                if (OUTPUT_MULTIPLE_RECORD_NODES.has(item.data.nodeType)) {
                    nodes.push({
                        label: item.data.name,
                        value: item.id,
                        icon: nodeTypeIconMap[item.data.nodeType]
                    })
                }
                return nodes
            }, []),
        [allParentNodes]
    )

    const actorUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes),
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const upstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes),
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const isNotAutomation = type !== 'automation'

    const emailAccount = useMemo(
        () => otherPlatformList?.find(item => item.type === senderAccountType),
        [otherPlatformList, senderAccountType]
    )

    if (!emailAccount) {
        return <UnitNoAuth type={senderAccountType} />
    }
    return (
        <>
            <CM.Container>
                <ActionAuthIntegrateCard type={senderAccountType} />
                <Divider color="var(--color-gray-200)" />
                <PopoverQuestion
                    type="recipients"
                    // arrowOffset={{ bottom: 10, left: -2}}
                    renderTarget={(node, ref) => (
                        <GroupRequired required label={<Text ref={ref}>收件人</Text>} tooltip={node}>
                            <SelectEmail
                                upstreamOptions={actorUpstreamOptions}
                                value={recipients}
                                onChange={recipients => onChange({ ...value, recipients })}
                            />
                        </GroupRequired>
                    )}
                />

                <Divider color="var(--color-gray-200)" />
                <PopoverQuestion
                    type="cc"
                    renderTarget={(node, ref) => (
                        <GroupRequired
                            label={<Text ref={ref}>抄送人</Text>}
                            mode="switch"
                            opened={enableCc}
                            onCollapseChange={enableCc => onChange({ ...value, enableCc })}
                            tooltip={node}
                        >
                            <Box px={8}>
                                <SelectEmail
                                    upstreamOptions={actorUpstreamOptions}
                                    value={cc}
                                    onChange={cc => onChange({ ...value, cc })}
                                />
                            </Box>
                        </GroupRequired>
                    )}
                />

                <Divider color="var(--color-gray-200)" />
                <GroupRequired required label="邮件标题">
                    <FormItem label="标题">
                        {/*
                         *@describe 此处placeholder过长时会引发删除变量时tiptapEditor的dom移位，进而引发不能聚焦
                         */}
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            placeholder="输入文字或添加变量"
                            value={title}
                            onBlur={title => onChange({ ...value, title })}
                            pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, DEVICE_ID_OPTION] : undefined}
                            upstreamOptions={upstreamOptions}
                            autoHeight
                            dataSourceList={dataSourceList}
                        />
                    </FormItem>
                </GroupRequired>

                <Divider color="var(--color-gray-200)" />
                <GroupRequired required label="邮件正文">
                    <FormItem label="编辑方式">
                        <SegmentedControl
                            data={emailEditType}
                            value={body?.editType}
                            onChange={nextEditType => onChange({ ...value, body: { ...body, editType: nextEditType as EMAIL_EDIT_TYPE } })}
                            fullWidth
                        />
                    </FormItem>
                    <Box px={8}>
                        <SendEmailProvider upstreamOptions={upstreamOptions} editType={editType} multiNodeOptions={multiNodeOptions}>
                            <EmailParagraph
                                value={body?.paragraph}
                                onChange={paragraph => onChange({ ...value, body: { ...body, paragraph } })}
                            />
                        </SendEmailProvider>
                    </Box>
                </GroupRequired>
                <EmailStyle value={value} onChange={onChange} />
            </CM.Container>
        </>
    )
}
